import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RoleName } from '@pbt/pbt-ui-components'

import FeatureToggle from '~/constants/featureToggle'
import {
  fetchRhapsodyPayConfig,
  getRhapsodyPayConfig,
} from '~/store/duck/rhapsodyPay'
import { getCurrentBusinessId } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import { shouldShowBraintreeFlow } from '~/utils/paymentFlowUtils'
import useHasRole from '~/utils/useHasRole'

/**
 * Minimal payment type for checking linked FA transactions.
 * Uses a subset of Payment to avoid requiring full GoPayment type in tests.
 */
interface PaymentWithGoTransaction {
  goTransaction?: object | null
}

/**
 * Hook to determine which payment flow to show (Braintree vs First American)
 * based on feature toggle, posProcessor setting, and Walker role override.
 *
 * The feature toggle acts as a safety guard - if disabled, the old flow is always used.
 * If enabled, the decision is based on posProcessor and Walker role.
 *
 * This hook proactively fetches rhapsodyPayConfig if it's not available.
 *
 * @param businessId - Optional business ID. If not provided, uses current business ID
 * @param payments - Optional payments to check for linked FA transactions (TODO CPAY-507)
 * @returns true if Braintree flow should be shown, false for First American flow
 */
export const usePaymentFlowDecision = (
  businessId?: string,
  payments?: PaymentWithGoTransaction[],
): boolean => {
  const dispatch = useDispatch()
  const currentBusinessId = useSelector(getCurrentBusinessId)
  const targetBusinessId = businessId || currentBusinessId || ''

  const isFeatureToggleEnabled = useSelector(
    getFeatureToggle(FeatureToggle.POS_TERMINAL_MIGRATION_TO_VERIFONE),
  )
  const rhapsodyPayConfig = useSelector(getRhapsodyPayConfig(targetBusinessId))
  const hasWalkerRole = useHasRole(RoleName.Walker, undefined, targetBusinessId)

  useEffect(() => {
    if (targetBusinessId && !rhapsodyPayConfig) {
      dispatch(fetchRhapsodyPayConfig(targetBusinessId))
    }
  }, [targetBusinessId, rhapsodyPayConfig, dispatch])

  if (!isFeatureToggleEnabled) {
    return false
  }

  // TODO CPAY-507 - Remove this once we integrate FA unlinked flow into V2 flow
  // If any payment has a linked First American transaction, use old flow
  if (payments?.some((payment) => payment.goTransaction != null)) {
    return false
  }

  return shouldShowBraintreeFlow(rhapsodyPayConfig?.posProcessor, hasWalkerRole)
}
